import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, Pause, Clock, Youtube, Podcast } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

export default function EpisodeCard({ ep, index = 0 }) {
    const { current, playing, playEpisode } = usePlayer();
    const isCurrent = current?.id === ep.id;
    const isPlaying = isCurrent && playing;
    const tilt = index % 2 ? "rotate-1" : "-rotate-1";

    return (
        <motion.article
            data-testid={`episode-card-${ep.id}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className={`group bg-cream border-2 border-ink hard-shadow ${tilt} transition-transform duration-300 hover:rotate-0 hover:-translate-y-1.5`}
        >
            <div className="relative overflow-hidden border-b-2 border-ink">
                <img
                    src={ep.poster_art}
                    alt={`${ep.title} poster`}
                    className="w-full aspect-[4/5] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                />
                <button
                    data-testid={`episode-play-${ep.id}`}
                    onClick={() => playEpisode(ep)}
                    aria-label={isPlaying ? `Pause ${ep.title}` : `Play ${ep.title}`}
                    className="absolute bottom-4 right-4 w-14 h-14 flex items-center justify-center rounded-full bg-pink text-cream border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:scale-110"
                >
                    {isPlaying ? (
                        <Pause className="w-6 h-6" fill="currentColor" />
                    ) : (
                        <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
                    )}
                </button>
                <span className="absolute top-4 left-4 bg-ink text-paper font-mono-brand text-xs uppercase tracking-[0.2em] px-3 py-1.5">
                    EP {ep.number}
                </span>
            </div>
            <div className="p-6 sm:p-7">
                <h3 className="font-display font-black text-xl sm:text-2xl text-ink leading-snug">
                    <Link
                        to={`/episodes/${ep.id}`}
                        data-testid={`episode-link-${ep.id}`}
                        className="transition-colors duration-150 hover:text-pink"
                    >
                        {ep.title}
                    </Link>
                </h3>
                <p className="mt-1 font-sub italic text-ink/70 text-sm">{ep.subtitle}</p>
                {ep.description && (
                    <p className="mt-3 text-sm leading-relaxed text-ink/80">{ep.description}</p>
                )}
                <p className="mt-4 text-sm leading-relaxed text-ink/80">
                    with <span className="font-bold text-pink">{ep.guest}</span>, {ep.guest_role}
                </p>
                <div className="mt-5 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 font-mono-brand text-xs text-ink border border-ink/30 px-2.5 py-1">
                        <Clock className="w-3.5 h-3.5" /> {ep.duration}
                    </span>
                    {ep.tags.map((t) => (
                        <span
                            key={t}
                            className="font-mono-brand text-xs uppercase tracking-wider bg-mustard/30 text-ink px-2.5 py-1 border border-ink/20"
                        >
                            {t}
                        </span>
                    ))}
                </div>
                <div className="mt-6 pt-5 border-t border-ink/15 flex items-center gap-5">
                    <a
                        href={ep.spotify_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`episode-spotify-${ep.id}`}
                        className="inline-flex items-center gap-1.5 font-sub font-bold text-sm text-ink hover:text-pink transition-colors duration-150"
                    >
                        <Podcast className="w-4 h-4" /> Spotify
                    </a>
                    <a
                        href={ep.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`episode-youtube-${ep.id}`}
                        className="inline-flex items-center gap-1.5 font-sub font-bold text-sm text-ink hover:text-pink transition-colors duration-150"
                    >
                        <Youtube className="w-4 h-4" /> YouTube
                    </a>
                </div>
            </div>
        </motion.article>
    );
}
